import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import WrForm from 'ember-wiser/components/form';
import type DpMeasuredJournalEvent from 'parog-web/models/dp-measured-journal-event';
import type TypedEmberChangeset from 'parog-web/utils/typed-ember-changeset';

interface Signature {
  Args: {
    measurement: TypedEmberChangeset<DpMeasuredJournalEvent>;
    onSubmit: () => Promise<void>;
    cyclesPocketInfusedVolume: (number | undefined)[];
    cyclesPocketDrainedVolume: (number | undefined)[];
  };
}

export default class EndSessionEditionForm extends Component<Signature> {
  get changesetErrors() {
    const occurredAtError = this.args.measurement.errors.find(
      e => e.key === 'occurredAtChronology'
    );

    return occurredAtError?.validation as string;
  }

  get cyclesPocketDrainedVolumeTotal() {
    const volumeSum = this.args.cyclesPocketDrainedVolume.reduce(
      (sum, value) => (sum || 0) + (value || 0),
      0
    ) as number;

    return volumeSum == 0 ? '-' : volumeSum;
  }

  get cyclesPocketInfusedVolumeTotal() {
    const volumeSum = this.args.cyclesPocketInfusedVolume.reduce(
      (sum, value) => (sum || 0) + (value || 0),
      0
    ) as number;

    return volumeSum == 0 ? '-' : volumeSum;
  }

  get cyclesUltrafiltrationVolumeTotal() {
    if (
      this.cyclesPocketInfusedVolumeTotal == '-' ||
      this.cyclesPocketDrainedVolumeTotal == '-'
    )
      return '-';

    return (
      this.cyclesPocketDrainedVolumeTotal - this.cyclesPocketInfusedVolumeTotal
    );
  }

  clearOccurredAtError = () => {
    this.args.measurement.removeError('occurredAtChronology');
  };
  <template>
    <WrForm @data={{@measurement}} @onSubmit={{@onSubmit}} as |form|>
      <form.InputRow
        @name='occurredAt'
        @label='Date et heure *'
        type='datetime-local'
        class={{if this.changesetErrors 'text-red border-b-red'}}
        {{on 'input' this.clearOccurredAtError}}
      >
        {{#if this.changesetErrors}}
          <div
            class='text-red pt-2 leading-[1.094rem] sm:pt-0'
            aria-label='occurredAtChronologyError'
          >{{this.changesetErrors}}</div>
        {{/if}}
      </form.InputRow>

      <form.InputRow
        @label='Poids de sortie'
        @name='endOutputWeight'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 80'
      >kg</form.InputRow>

      <form.InputRow
        @label='Pression artérielle systolique'
        @name='endArterialSystolicPressure'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 120'
      >mmHg</form.InputRow>

      <form.InputRow
        @label='Pression artérielle diastolique'
        @name='endArterialDiastolicPressure'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 75'
      >mmHg</form.InputRow>

      <form.InputRow
        @label='Fréquence cardiaque'
        @name='endHeartRate'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 70'
      >mmHg</form.InputRow>

      <form.Row aria-label='cyclesPocketInfusedVolumeTotal'>
        <:header> Volume total des poches infusées</:header>
        <:col1>
          {{this.cyclesPocketInfusedVolumeTotal}}
        </:col1>
        <:col2>
          <div class='mb-2 sm:mb-0'> L</div>
        </:col2>
      </form.Row>

      <form.Row aria-label='cyclesPocketDrainedVolumeTotal'>
        <:header>Volume total des poches drainées</:header>
        <:col1>
          {{this.cyclesPocketDrainedVolumeTotal}}
        </:col1>
        <:col2>
          <div class='mb-2 sm:mb-0'> L</div>
        </:col2>
      </form.Row>

      <form.Row aria-label='cyclesUltrafiltrationVolumeTotal'>
        <:header> Volume total d’ultrafiltration</:header>
        <:col1>
          {{this.cyclesUltrafiltrationVolumeTotal}}
        </:col1>
        <:col2>
          <div class='mb-2 sm:mb-0'> L</div>
        </:col2>
      </form.Row>

      <form.TextareaRow
        @label='Commentaires patient'
        @name='comments'
        class='mt-2 sm:mt-0'
        placeholder='Laisser un commentaire…'
      >
        &nbsp;
      </form.TextareaRow>

      {{! Hack: add a hidden native submit button so that
          pressing "Enter" triggers a proper HTML form submission. }}
      <button type='submit' class='hidden'></button>

    </WrForm>
  </template>
}
